import React, { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";

const colorAt = (pct) =>
  pct >= 0.95 ? "hsl(var(--destructive))" :
  pct >= 0.8  ? "hsl(var(--warning))" :
  pct >= 0.6  ? "hsl(var(--primary))" :
                "hsl(var(--muted-foreground))";

export const ZoneForecastTable = ({ zones = [], onZoneClick }) => {
  const horizons = useMemo(() => {
    if (!zones.length) return [15, 30, 60];
    return zones[0].forecasts?.map((f) => f.horizon_min) ?? [15, 30, 60];
  }, [zones]);

  const [sortBy, setSortBy] = useState(60);

  const sorted = useMemo(() => {
    return [...zones].sort((a, b) => {
      const A = a.forecasts?.find((f) => f.horizon_min === sortBy)?.pct ?? 0;
      const B = b.forecasts?.find((f) => f.horizon_min === sortBy)?.pct ?? 0;
      return B - A;
    });
  }, [zones, sortBy]);

  return (
    <div className="panel" data-testid="zone-forecast-table">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
        <span className="h-widget">Per-Zone Projection</span>
        <div className="flex items-center gap-1">
          <span className="micro-label mr-1">Sort by</span>
          {horizons.map((h) => (
            <button
              key={h}
              onClick={() => setSortBy(h)}
              data-testid={`forecast-sort-${h}`}
              className={[
                "px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                sortBy === h
                  ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                  : "border-border text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              T+{h}m
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-[hsl(var(--surface-elevated))]">
              <th className="text-left px-3 py-2 micro-label">Zone</th>
              <th className="text-right px-3 py-2 micro-label">Current</th>
              {horizons.map((h) => (
                <th key={h} className="text-right px-3 py-2 micro-label">T+{h}m</th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((z) => {
              const curPct = z.capacity ? z.current_occupancy / z.capacity : 0;
              return (
                <tr
                  key={z.zone_id}
                  className="border-b border-border hover:bg-[hsl(var(--surface-elevated))] cursor-pointer"
                  onClick={() => onZoneClick?.(z.zone_id, z.zone_name)}
                  data-testid={`forecast-row-${z.zone_id}`}
                >
                  <td className="px-3 py-2">
                    <div className="font-display font-bold uppercase tracking-tight text-[11px]">{z.zone_name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{z.zone_type} · cap {(z.capacity ?? 0).toLocaleString("en-IN")}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="font-mono" style={{ color: colorAt(curPct) }}>{Math.round(curPct * 100)}%</span>
                  </td>
                  {horizons.map((h) => {
                    const f = z.forecasts?.find((x) => x.horizon_min === h);
                    const pct = f?.pct ?? 0;
                    return (
                      <td key={h} className="px-3 py-2 text-right">
                        <div className="inline-flex flex-col items-end">
                          <span className="font-mono font-semibold" style={{ color: colorAt(pct) }}>{Math.round(pct * 100)}%</span>
                          <div className="h-1 w-16 bg-border mt-0.5">
                            <div className="h-full" style={{ width: `${Math.min(100, pct * 100)}%`, background: colorAt(pct) }} />
                          </div>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-2">
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={horizons.length + 3} className="px-3 py-6 text-center text-xs text-muted-foreground">No forecasts available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ZoneForecastTable;
